import {$qs, $qsAll} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import {observeCheckoutHTML, onCheckoutSubmitErrorManual, onCheckoutSubmitManual, tokenizedMethodSelected} from '../checkout';
import {Feature} from '../features';
import {PoyntCardElement} from './class-poynt-card-payment';
import {type CollectOptions, type NoncePayload} from './types';
import {insertCaptchaFields, cleanupCaptchaFields} from '../botProtection';
import {cleanupTransactionFields, createPaymentTransaction, insertTransactionFields, updatePaymentTransaction} from '../transaction';

async function initPoyntPayments($checkoutForm: HTMLFormElement): Promise<void> {
	if (!Feature.enabled(FeatureFlag.POYNT_GATEWAYS)) {
		return;
	}

	const poyntSrc = Feature.metadata<string>(FeatureFlag.POYNT_GATEWAYS, 'poynt_src');
	const businessId = Feature.metadata<string>(FeatureFlag.POYNT_GATEWAYS, 'business_id');
	const applicationId = Feature.metadata<string>(FeatureFlag.POYNT_GATEWAYS, 'application_id');

	if (!businessId || !applicationId || !poyntSrc) {
		return;
	}

	if (Feature.enabled(FeatureFlag.POYNT_CARD_GATEWAY)) {
		const cardElement = new PoyntCardElement({businessId, applicationId});

		const options: CollectOptions = {
			displayComponents: {
				labels: true,
			},
			iFrame: {
				border: '0px',
				borderRadius: '0px',
				boxShadow: 'none',
				height: '232px',
				width: 'auto',
			},
			style: {
				theme: 'default',
			},
		};

		const cardElementContainerId = 'pp-poynt-card-element';
		cardElement.mount(cardElementContainerId, options);
		observeCheckoutHTML(() => {
			const $container = $qs('#' + cardElementContainerId);
			if (!$container) {
				return;
			}

			if ($container.innerHTML === '') {
				cardElement.mount(cardElementContainerId, options);
			}
		});

		onCheckoutSubmitManual(
			$checkoutForm,
			['peachpay_poynt_card'],
			async gatewayId => {
				$qsAll('.peachpay-tokenize-error', $el => {
					$el.classList.add('hide');
					$el.innerHTML = '';
				});

				cleanupTransactionFields();
				cleanupTokenFields();
				cleanupCaptchaFields();

				if (tokenizedMethodSelected(gatewayId)) {
					const transactionId = await createPaymentTransaction(gatewayId);
					if (!transactionId) {
						return false;
					}

					insertTransactionFields($checkoutForm, transactionId);
					await insertCaptchaFields($checkoutForm);
				} else {
					const payload = getNoncePayload($checkoutForm);
					const {nonce, error} = await cardElement.tokenize(payload);

					const transactionId = await createPaymentTransaction(gatewayId);
					if (!transactionId) {
						return false;
					}

					if (error ?? !nonce) {
						await updatePaymentTransaction(transactionId, {
							paymentStatus: 'creationfailed',
							orderStatus: 'unperformed',
							note: error ?? 'An unknown error occured while retrieving the nonce.',
						});

						const target = '.payment_box:not([style*="display:none"]):not([style*="display: none"])';

						$qsAll(`${target} .peachpay-tokenize-error`, $el => {
							$el.classList.remove('hide');
							$el.innerHTML = error ?? 'An unknown error occured while tokenizing the payment method.';
						});

						return false;
					}

					insertTokenFields($checkoutForm, nonce);
					insertTransactionFields($checkoutForm, transactionId);
					await insertCaptchaFields($checkoutForm);
				}

				return true;
			},
		);

		onCheckoutSubmitErrorManual($checkoutForm, ['peachpay_poynt_card'], async (transactionId, errors) => {
			cleanupTransactionFields();
			cleanupTokenFields();
			cleanupCaptchaFields();

			if (!transactionId) {
				return;
			}

			try {
				await updatePaymentTransaction(transactionId, {
					paymentStatus: 'unperformed',
					orderStatus: 'creationfailed',
					note: errors.join('\n'),
				});
			} catch {
				// Do no harm.
			}
		});
	}
}

function cleanupTokenFields() {
	$qsAll('input[name="peachpay_poynt_nonce"]', $el => {
		$el.remove();
	});
}

function insertTokenFields($checkoutForm: HTMLFormElement, nonce: string) {
	// insert nonce data fields
	const $poyntNonce = document.createElement('input');
	$poyntNonce.type = 'hidden';
	$poyntNonce.name = 'peachpay_poynt_nonce';
	$poyntNonce.value = nonce;
	$checkoutForm.insertAdjacentElement('beforeend', $poyntNonce);
}

function getNoncePayload($checkoutForm: HTMLFormElement): NoncePayload {
	const payload = {} as NoncePayload;
	const formData = new FormData($checkoutForm);

	if (formData.has('billing_email')) {
		payload.emailAddress = formData.get('billing_email') as string;
	}

	if (formData.has('billing_first_name')) {
		payload.firstName = formData.get('billing_first_name') as string;
	}

	if (formData.has('billing_last_name')) {
		payload.lastName = formData.get('billing_last_name') as string;
	}

	if (formData.has('billing_address_1')) {
		payload.line1 = formData.get('billing_address_1') as string;
	}

	if (formData.has('billing_postcode')) {
		payload.zipCode = formData.get('billing_postcode') as string;
	}

	return payload;
}

export {initPoyntPayments};
